import { BarIndicator, PacmanIndicator } from '@/components/general/activity-indicators';
import { MB_Button } from '@/components/general/button';
import { MB_Image } from '@/components/general/image';
import { mbHidePopUp, mbShowPopUp } from '@/components/general/pop-up';
import { MB_ThumbnailVideoViewerModal } from '@/components/general/video-player';
import { mbApplyTransparency, mbTextStyles } from '@/utils/general/style-utils';
import { MB_TextInput } from '@/components/general/text-input';
import { MB_Modal } from '@/components/general/modal';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Platform, StyleProp, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native';
import { FlatGrid } from 'react-native-super-grid';
import AntDesign from 'react-native-vector-icons/AntDesign';
import Feather from 'react-native-vector-icons/Feather';
import { COLORS } from '../../../../constants/colors';
import { SERVER_ERROR_CODES, STRING_CONSTANTS } from '../../../../constants/constants';
import { textStyles } from '../../../../constants/textStyles';
import { useDeleteImage, useEditImage, useGetImagePoolImages, useReorderImages } from '../../../../hooks/imagePoolHooks';
import { useDeleteVideo, useEditVideo, useGetVideoPoolVideos, useReorderVideos } from '../../../../hooks/videoPoolHooks';
import { useGetFolders } from '../../../../hooks/adminHooks';
import { MoveToFolderModal } from './MoveToFolderModal';
import { FolderReorderGrid } from './FolderReorderGrid';
import { PoolImage, RESOURCE_POOL_TYPE, PoolVideo, Folder, FOLDER_TYPE } from '../../../../typesAndInterfaces/typesAndInterfaces';
import { MB_UseInfiniteFetchOptions, useMBInfiniteFetch } from '@/utils/general/util-hooks';
import { EmptyTableIcon } from '../../../../resources/svgComponents/EmptyTableIcon';
import { imageUtils } from '../../../../utils/imageUtils';
import { FoldersView } from '../../../helperComponents/FolderView/FolderView';

interface ImageVideoPoolFlatListProps<T extends RESOURCE_POOL_TYPE> {
  folder?: Folder;
  onFolderPress?: (folder: Folder) => void;
  resourcePoolType: T;
  style?: StyleProp<ViewStyle>;
  disableDelete?: boolean;
  selectedItem?: T extends RESOURCE_POOL_TYPE.Image ? PoolImage : PoolVideo;
  onSelectItem?: (item: T extends RESOURCE_POOL_TYPE.Image ? PoolImage : PoolVideo) => void;
  disableAddFolder?: boolean;
}

const DefaultLoadingSpinner = () => {
  return (
    <View style={{ justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', minWidth: 100 }}>
      <PacmanIndicator style={styles.defaultLoader} color={COLORS.white} />
      <Text style={textStyles.smallText}>Loading...</Text>
    </View>
  );
};

interface RenderPoolImageProps {
  item: PoolImage;
  index: number;
  onDelete?: (poolImage: PoolImage) => void;
  onEdit?: (poolImage: PoolImage) => void;
  isSelected?: boolean;
  onPress?: (image: PoolImage) => void;
  folderBadge?: string;
}

interface RenderPoolVideoProps {
  item: PoolVideo;
  index: number;
  onDelete?: (poolVideo: PoolVideo) => void;
  onEdit?: (poolVideo: PoolVideo) => void;
  isSelected?: boolean;
  onPress?: (video: PoolVideo) => void;
  folderBadge?: string;
}

const RenderPoolImageLoading = memo(() => {
  return (
    <View style={[styles.cardContainer, { alignItems: 'center', justifyContent: 'center' }]}>
      <BarIndicator color={'white'} count={4} />
    </View>
  );
});

const RenderPoolVideo = memo(({ item: poolVideo, isSelected, onPress, onDelete, onEdit, folderBadge }: RenderPoolVideoProps) => {
  return (
    <View style={[styles.cardContainer, { paddingBottom: 12 }, isSelected && styles.cardSelected]}>
      {folderBadge !== undefined && (
        <View style={styles.folderBadge}>
          <Feather name="folder" size={10} color={COLORS.textLightPurple} />
          <Text style={styles.folderBadgeText} numberOfLines={1}>
            {folderBadge}
          </Text>
        </View>
      )}
      <MB_ThumbnailVideoViewerModal
        video={poolVideo}
        thumbnailContainerStyle={styles.thumbnailContainer}
        isFocused={true}
        customVideoProcessingView={<Text style={[textStyles.smallText]}>We're processing this video.{'\n'}Please check back later.</Text>}
        hidePlayIcon={!poolVideo.thumbnail || onPress !== undefined}
        disableModalVideo={onPress !== undefined}
        onThumbnailPressed={() => onPress?.(poolVideo)}
      />
      <TouchableOpacity disabled={!onPress} onPress={() => onPress?.(poolVideo)}>
        <Text style={styles.cardTitle}>{poolVideo.name}</Text>
      </TouchableOpacity>
      {onEdit && <MB_Button style={styles.editButton} leftElement={<Feather name={'edit-2'} size={13} color={COLORS.white} />} onPress={() => onEdit(poolVideo)} />}
      {onDelete && <MB_Button style={styles.deleteButton} leftElement={<AntDesign name={'close'} size={16} color={COLORS.lighterGray} />} onPress={() => onDelete(poolVideo)} />}
    </View>
  );
});

const RenderPoolImage = memo(({ item: poolImage, onDelete, onEdit, isSelected, onPress, folderBadge }: RenderPoolImageProps) => {
  return (
    <View style={[styles.cardContainer, isSelected && styles.cardSelected]}>
      {folderBadge !== undefined && (
        <View style={styles.folderBadge}>
          <Feather name="folder" size={10} color={COLORS.textLightPurple} />
          <Text style={styles.folderBadgeText} numberOfLines={1}>
            {folderBadge}
          </Text>
        </View>
      )}
      <TouchableOpacity disabled={!onPress} style={styles.imagePressableWrapper} onPress={() => onPress?.(poolImage)}>
        <MB_Image source={poolImage.small ?? imageUtils.images.brokenImage} style={styles.imageContainerStyle} resizeMode={'contain'} disableInteraction />
        <Text style={styles.cardTitle}>{poolImage.name}</Text>
      </TouchableOpacity>
      {onEdit && <MB_Button style={styles.editButton} leftElement={<Feather name={'edit-2'} size={13} color={COLORS.white} />} onPress={() => onEdit(poolImage)} />}
      {onDelete && <MB_Button style={styles.deleteButton} leftElement={<AntDesign name={'close'} size={16} color={COLORS.lighterGray} />} onPress={() => onDelete(poolImage)} />}
    </View>
  );
});

const ImageVideoPoolFlatListBeforeMemo = <T extends RESOURCE_POOL_TYPE>({
  folder,
  onFolderPress,
  disableAddFolder,
  resourcePoolType,
  style,
  disableDelete,
  selectedItem,
  onSelectItem,
}: ImageVideoPoolFlatListProps<T>) => {
  const [searchText, setSearchText] = useState('');
  const debounceId = useRef<NodeJS.Timeout>();
  const { mutate: deleteImage } = useDeleteImage();
  const { mutate: deleteVideo } = useDeleteVideo();
  const { mutate: editImage } = useEditImage();
  const { mutate: editVideo } = useEditVideo();

  const deleteHandler = resourcePoolType === RESOURCE_POOL_TYPE.Image ? deleteImage : deleteVideo;

  const [renameTarget, setRenameTarget] = useState<PoolImage | PoolVideo | undefined>(undefined);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState(false);

  // Folder management modes. Only in the real admin pool screens (the item-picker
  // modal passes onSelectItem/disableDelete and gets none of this).
  const allowManage = !onSelectItem && !disableDelete;
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const { mutate: reorderImages } = useReorderImages();
  const { mutate: reorderVideos } = useReorderVideos();
  const { data: allFolders } = useGetFolders({
    type: resourcePoolType === RESOURCE_POOL_TYPE.Image ? FOLDER_TYPE.image : FOLDER_TYPE.video,
    queryOptions: { enabled: allowManage && !folder },
  });

  const onEdit = useCallback((item: PoolImage | PoolVideo) => {
    setRenameTarget(item);
    setRenameValue(item.name);
    setRenameError(false);
  }, []);

  const onRenameConfirm = useCallback(() => {
    const trimmed = renameValue.trim();
    if (!renameTarget || trimmed.length === 0) {
      setRenameError(true);
      return;
    }
    const callbacks = {
      onSuccess: () => setRenameTarget(undefined),
      onError: () => setRenameError(true),
    };
    if (resourcePoolType === RESOURCE_POOL_TYPE.Image) {
      editImage({ imageId: renameTarget._id, name: trimmed }, callbacks);
    } else {
      editVideo({ videoId: renameTarget._id, name: trimmed }, callbacks);
    }
  }, [editImage, editVideo, renameTarget, renameValue, resourcePoolType]);

  const getPoolImagesHook = useGetImagePoolImages({
    totalItemsPerPage: 20,
    searchText,
    folderId: folder?._id,
    queryOptions: { enabled: resourcePoolType === RESOURCE_POOL_TYPE.Image },
  });
  const getPoolVideosHook = useGetVideoPoolVideos({
    totalItemsPerPage: 20,
    searchText,
    folderId: folder?._id,
    queryOptions: { enabled: resourcePoolType === RESOURCE_POOL_TYPE.Video },
  });

  const getPoolHandler = resourcePoolType === RESOURCE_POOL_TYPE.Image ? getPoolImagesHook : getPoolVideosHook;

  const { data, loadMore, isLoading, isFetching } = useMBInfiniteFetch<PoolImage | PoolVideo, MB_UseInfiniteFetchOptions>(getPoolHandler, { prefillWhileLoading: true });

  const flatListStyleFinal = useMemo(() => [styles.flatListStyle, style], [style]);

  const keyExtractor = React.useMemo(() => (item: PoolImage | string) => typeof item === 'string' ? item : item._id, []);

  const onDelete = useCallback(
    (poolImageVideo: PoolImage | PoolVideo) => {
      mbShowPopUp({
        title: resourcePoolType === RESOURCE_POOL_TYPE.Image ? 'Delete image' : 'Delete video',
        message: `Are you sure you want to delete “${poolImageVideo.name}”?`,
        buttonText: resourcePoolType === RESOURCE_POOL_TYPE.Image ? 'Delete image' : 'Delete video',
        buttonAction: () => {
          return new Promise((resolve, reject) => {
            deleteHandler(poolImageVideo._id, {
              onSuccess: () => {
                resolve();
                mbHidePopUp();
              },
              onError: error => {
                console.error('Error when deleting image or video: ', error);
                if (error.errorCode === SERVER_ERROR_CODES.IMAGE_USED || error.errorCode === SERVER_ERROR_CODES.VIDEO_USED_ON_ANSWER) {
                  reject(error.message);
                } else {
                  reject(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
                }
              },
            });
          });
        },
        buttonStyle: {
          container: { backgroundColor: COLORS.errorColor },
        },
        containerStyle: {
          width: 454,
        },
        secondaryButtonText: 'Cancel',
        secondaryButtonStyle: {
          text: { color: COLORS.buttonPurple },
          container: { backgroundColor: COLORS.white },
        },
      });
    },
    [deleteHandler, resourcePoolType]
  );

  const toggleSelected = useCallback((item: PoolImage | PoolVideo) => {
    setSelectedIds(previous => (previous.includes(item._id) ? previous.filter(id => id !== item._id) : [...previous, item._id]));
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedIds([]);
  }, []);

  const onPersistOrder = useCallback(
    (itemsOrder: { itemId: string; order: number }[]) => {
      if (resourcePoolType === RESOURCE_POOL_TYPE.Image) {
        reorderImages(itemsOrder.map(({ itemId, order }) => ({ imageId: itemId, order })));
      } else {
        reorderVideos(itemsOrder.map(({ itemId, order }) => ({ videoId: itemId, order })));
      }
    },
    [reorderImages, reorderVideos, resourcePoolType]
  );

  const folderNamesById = useMemo(() => new Map((allFolders ?? []).map(currentFolder => [currentFolder._id, currentFolder.name])), [allFolders]);

  const renderItemModifed = useCallback(
    ({ item, index }: { item: PoolImage | PoolVideo | string; index: number }) => {
      if (isLoading || typeof item === 'string') {
        return <RenderPoolImageLoading />;
      }

      const folderBadge = !folder && searchText.trim() !== '' && item.folderId ? folderNamesById.get(item.folderId) ?? 'In folder' : undefined;

      if (isSelectMode) {
        const commonProps = {
          index,
          isSelected: selectedIds.includes(item._id),
          folderBadge,
        };
        if (resourcePoolType === RESOURCE_POOL_TYPE.Video) {
          return <RenderPoolVideo item={item as PoolVideo} {...commonProps} onPress={toggleSelected} />;
        }
        return <RenderPoolImage item={item as PoolImage} {...commonProps} onPress={toggleSelected} />;
      }

      if (resourcePoolType === RESOURCE_POOL_TYPE.Video) {
        return (
          <RenderPoolVideo
            item={item as PoolVideo}
            index={index}
            onDelete={onDelete}
            onEdit={disableDelete ? undefined : onEdit}
            isSelected={selectedItem?._id === item._id}
            onPress={onSelectItem}
            folderBadge={folderBadge}
          />
        );
      } else if (resourcePoolType === RESOURCE_POOL_TYPE.Image) {
        return (
          <RenderPoolImage
            item={item}
            index={index}
            isSelected={selectedItem?._id === item._id}
            onPress={onSelectItem as (item: PoolImage) => void}
            onDelete={disableDelete ? undefined : onDelete}
            onEdit={disableDelete ? undefined : onEdit}
            folderBadge={folderBadge}
          />
        );
      }

      return null;
    },
    [disableDelete, folder, folderNamesById, isLoading, isSelectMode, onDelete, onEdit, onSelectItem, resourcePoolType, searchText, selectedIds, selectedItem?._id, toggleSelected]
  );

  const onSearchChange = useCallback((text: string) => {
    clearTimeout(debounceId.current);
    debounceId.current = setTimeout(() => {
      setSearchText(text);
    }, 500);
  }, []);

  const items = useMemo(() => {
    // TODO: Anas: It might be worth actually receiving the videos from backend without processing entries for this.
    if (resourcePoolType === RESOURCE_POOL_TYPE.Video && onSelectItem) {
      return (data as PoolVideo[]).filter(item => !item.isProcessing);
    }
    return data;
  }, [data, onSelectItem, resourcePoolType]);

  const ListHeaderComponent = useMemo(() => {
    return (
      <>
        {!folder && (
          <FoldersView
            type={resourcePoolType === RESOURCE_POOL_TYPE.Image ? FOLDER_TYPE.image : FOLDER_TYPE.video}
            disableAddFolder={disableAddFolder}
            onFolderPress={onFolderPress}
          />
        )}
        <Text style={styles.title}>{resourcePoolType}s</Text>
        <View style={styles.listHeader}>
          <View style={styles.searchIcon}>
            <Feather name="search" size={22} color={COLORS.white} />
          </View>
          <MB_TextInput
            placeholder={resourcePoolType === RESOURCE_POOL_TYPE.Image ? 'Search images by name or tags' : 'Search videos by name or tags'}
            style={styles.searchInput}
            onChangeText={onSearchChange}
          />
        </View>

        {allowManage && !isReorderMode && (
          <View style={styles.manageBar}>
            <MB_Button
              title={isSelectMode ? 'Cancel' : 'Select'}
              style={styles.manageButton}
              textStyle={{ color: COLORS.buttonPurple }}
              onPress={() => (isSelectMode ? exitSelectMode() : setIsSelectMode(true))}
            />
            {isSelectMode && (
              <MB_Button
                title={`Move ${selectedIds.length} to…`}
                disabled={selectedIds.length === 0}
                style={styles.movePrimaryButton}
                onPress={() => setIsMoveModalVisible(true)}
              />
            )}
            {!isSelectMode && folder && Platform.OS === 'web' && (
              <MB_Button title="Reorder" style={styles.manageButton} textStyle={{ color: COLORS.buttonPurple }} onPress={() => setIsReorderMode(true)} />
            )}
          </View>
        )}
        {allowManage && isReorderMode && (
          <View style={styles.manageBar}>
            <Text style={styles.reorderHint}>Drag items to reorder — changes save automatically.</Text>
            <MB_Button title="Done" style={styles.movePrimaryButton} onPress={() => setIsReorderMode(false)} />
          </View>
        )}

        {items.length < 1 && (
          <View style={styles.emptyDataContainer}>
            <EmptyTableIcon />
            <Text style={[textStyles.normalText, { color: COLORS.lighterGray, marginTop: 4 }]}>No Images/Videos</Text>
          </View>
        )}
      </>
    );
  }, [allowManage, disableAddFolder, exitSelectMode, folder, isReorderMode, isSelectMode, items.length, onFolderPress, onSearchChange, resourcePoolType, selectedIds.length]);

  if (allowManage && isReorderMode && folder) {
    return (
      <View style={flatListStyleFinal}>
        {ListHeaderComponent}
        <FolderReorderGrid
          resourcePoolType={resourcePoolType}
          items={items.filter((item): item is PoolImage | PoolVideo => typeof item !== 'string')}
          boxesPerRow={5}
          onPersistOrder={onPersistOrder}
        />
      </View>
    );
  }

  return (
    <>
      <FlatGrid
        keyExtractor={keyExtractor}
        itemDimension={144 + 6} //  Width of the item + extra space for delete button (144 + 6)
        spacing={18}
        data={items}
        renderItem={renderItemModifed}
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        style={flatListStyleFinal}
        ListFooterComponent={isFetching && !isLoading ? DefaultLoadingSpinner : null}
        ListHeaderComponent={ListHeaderComponent}
      />

      <MoveToFolderModal
        key={`move-modal-${isMoveModalVisible}`}
        isVisible={isMoveModalVisible}
        resourcePoolType={resourcePoolType}
        items={items.filter((item): item is PoolImage | PoolVideo => typeof item !== 'string' && selectedIds.includes(item._id))}
        currentFolderId={folder?._id}
        onDismiss={didMove => {
          setIsMoveModalVisible(false);
          if (didMove) {
            exitSelectMode();
          }
        }}
      />

      <MB_Modal
        isVisible={renameTarget !== undefined}
        onDismiss={() => setRenameTarget(undefined)}
        title={`Rename ${resourcePoolType === RESOURCE_POOL_TYPE.Image ? 'image' : 'video'}`}
        childrenWrapperStyle={styles.renameModal}
      >
        <Text style={styles.renameLabel}>Name*</Text>
        <MB_TextInput
          value={renameValue}
          onChangeText={text => {
            setRenameValue(text);
            setRenameError(false);
          }}
          isError={renameError}
        />
        <View style={styles.renameButtons}>
          <MB_Button title="Cancel" style={styles.renameCancelButton} textStyle={{ color: COLORS.buttonPurple }} onPress={() => setRenameTarget(undefined)} />
          <MB_Button title="Save" style={styles.renameSaveButton} onPress={onRenameConfirm} />
        </View>
      </MB_Modal>
    </>
  );
};

export const ImageVideoPoolFlatList = React.memo(ImageVideoPoolFlatListBeforeMemo) as typeof ImageVideoPoolFlatListBeforeMemo;

const styles = StyleSheet.create({
  title: mbTextStyles([
    textStyles.smallText,
    {
      marginBottom: 16,
      textAlign: 'left',
    },
  ]),
  flatListStyle: {
    width: '100%',
    flex: 1,
    paddingTop: 0,
  },
  emptyDataContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '100%',
    flex: 1,
  },
  deleteButton: {
    width: 24,
    height: 24,
    borderRadius: 300,
    position: 'absolute',
    backgroundColor: 'red',
    top: -6,
    right: -6,
    paddingHorizontal: 0,
  },
  editButton: {
    width: 24,
    height: 24,
    borderRadius: 300,
    position: 'absolute',
    backgroundColor: COLORS.buttonPurple,
    top: -6,
    right: 22,
    paddingHorizontal: 0,
  },
  renameModal: {
    width: 420,
    padding: 24,
  },
  renameLabel: mbTextStyles([textStyles.normalText, { textAlign: 'left', marginBottom: 8 }]),
  renameButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  renameCancelButton: {
    backgroundColor: COLORS.white,
    marginRight: 12,
    paddingHorizontal: 20,
  },
  renameSaveButton: {
    backgroundColor: COLORS.buttonPurple,
    paddingHorizontal: 24,
  },
  cardContainer: {
    width: 144,
    height: 170,
    borderRadius: 8,
    backgroundColor: COLORS.backgroundPurple,
  },
  videoPoolContainer: {
    backgroundColor: mbApplyTransparency(COLORS.black, 0.3),
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePressableWrapper: {
    flex: 1,
    paddingVertical: 16,
  },
  imageContainerStyle: {
    flex: 1,
    aspectRatio: 1,
  },
  defaultLoader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    marginTop: 15,
  },
  cardTitle: StyleSheet.flatten([
    textStyles.smallText,
    {
      marginTop: 12,
    },
  ]),
  cardSelected: {
    borderWidth: 1,
    borderColor: COLORS.white,
  },
  thumbnailContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  listHeader: {
    width: '100%',
    paddingHorizontal: 18,
    marginBottom: 20,
  },
  manageBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 18,
    marginBottom: 16,
    columnGap: 12,
  },
  manageButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: COLORS.buttonPurple,
    height: 32,
    borderRadius: 8,
    paddingHorizontal: 14,
  },
  movePrimaryButton: {
    backgroundColor: COLORS.buttonPurple,
    height: 32,
    borderRadius: 8,
    paddingHorizontal: 14,
  },
  reorderHint: mbTextStyles([
    textStyles.smallText,
    {
      flex: 1,
      textAlign: 'left',
      color: COLORS.textLightPurple,
    },
  ]),
  folderBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    zIndex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: mbApplyTransparency(COLORS.black, 0.6),
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    maxWidth: 120,
  },
  folderBadgeText: mbTextStyles([
    textStyles.tinyText,
    {
      color: COLORS.textLightPurple,
      marginStart: 4,
    },
  ]),
  searchIcon: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    paddingHorizontal: 15,
  },
  searchInput: {
    paddingLeft: 43,
    width: '100%',
  },
});
